/**
 * @fileoverview Defines the `Toast` component, a reusable notification element. It uses the `ToastProps` interface for configuration and includes a `CloseIcon`. The `Toast` component displays a message and automatically closes after a specified duration.
 */

import React, { useEffect, useRef, useState } from 'react'
import '../../styles/components/Toast.css'

export interface ToastProps {
  message: string
  duration?: number
  onClose: () => void
  targetElement?: HTMLElement | null
}

const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
)

export const Toast: React.FC<ToastProps> = ({
  message,
  duration = 2000,
  onClose,
  targetElement,
}) => {
  const toastRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({ top: 0, left: 0 })

  useEffect(() => {
    const timer = setTimeout(() => {
      onClose()
    }, duration)

    return () => clearTimeout(timer)
  }, [duration, onClose])

  useEffect(() => {
    if (targetElement && toastRef.current) {
      const targetRect = targetElement.getBoundingClientRect()
      const toastRect = toastRef.current.getBoundingClientRect()

      // Position above the button with some spacing
      const top = targetRect.top - toastRect.height - 8
      const left = targetRect.left + targetRect.width / 2 - toastRect.width / 2

      // Keep popup within viewport bounds
      const viewportWidth = window.innerWidth
      const adjustedLeft = Math.max(8, Math.min(left, viewportWidth - toastRect.width - 8))
      const adjustedTop = Math.max(8, top)

      setPosition({
        top: adjustedTop,
        left: adjustedLeft,
      })
    }
  }, [targetElement])

  return (
    <div
      ref={toastRef}
      className="toast-popup"
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 1000,
      }}
    >
      <div className="toast-popup-content">
        <span className="toast-popup-message">{message}</span>
        <button className="toast-popup-close" onClick={onClose}>
          <CloseIcon />
        </button>
      </div>
      <div className="toast-popup-arrow"></div>
    </div>
  )
}

export default Toast
